/*
 * «Quién recibe esta alerta por mail» — la lógica pura (tanda «Control de líneas», 15/10).
 *
 * Una alerta le llega a una persona si está en el equipo del cliente y tiene prendido ese aviso en «Mis
 * alertas». El backend contesta las dos cosas (`team` + `alerts`, contrato §4.3); acá se decide qué
 * fila es de qué aviso, con qué texto se nombra y con qué interruptor se prende.
 *
 * Las claves son los `indicator` de la Bitácora, salvo `ods_critic`, que se parte en sus tres
 * interruptores (`ods_vs_today` / `_yesterday` / `_last_7_days`): con una sola clave no se puede decir
 * «Lo recibes» ni ofrecer «Activar». Si el backend la manda igual sin partir, se lee pero sin «Activar».
 */

// En el orden de «Mis alertas». `labelKey` es el MISMO texto de su interruptor allá, así lo que la
// persona activa y lo que esta pantalla nombra no pueden decir cosas distintas; `slug` es su endpoint.
export const RECIPIENT_ALERTS = [
    {key: 'url_down', labelKey: 'user_email_alerts.url_down', slug: 'url-email-alert'},
    {key: 'no_impressions', labelKey: 'user_email_alerts.no_impressions', slug: 'impression-email-alert'},
    {key: 'cost_vs_time', labelKey: 'user_email_alerts.critical_cost_vs_time', slug: 'cost-vs-time-email-alert'},
    {key: 'ods_vs_yesterday', labelKey: 'user_email_alerts.critical_yesterday_vs_ideal', slug: 'ods-vs-yesterday-email-alert'},
    {key: 'ods_vs_last_7_days', labelKey: 'user_email_alerts.critical_last_7_days_vs_ideal', slug: 'ods-vs-last-7-days-email-alert'},
    {key: 'ods_vs_today', labelKey: 'user_email_alerts.critical_today_vs_ideal', slug: 'ods-vs-today-email-alert'},
    {key: 'ods_vs_platform_budget', labelKey: 'user_email_alerts.ods_vs_platform_budget', slug: 'ods-vs-platform-budget-email-alert'},
    {key: 'roas_vs_last_period', labelKey: 'user_email_alerts.critical_roas_vs_last_period', slug: 'roas-vs-last-period-email-alert'},
    {key: 'acos_vs_last_period', labelKey: 'user_email_alerts.critical_acos_vs_last_period', slug: 'acos-vs-last-period-email-alert'},
    {key: 'budget_exceeded', labelKey: 'user_email_alerts.budget_exceeded', slug: 'budget-exceeded-email-alert'},
    {key: 'uncovered_spend', labelKey: 'user_email_alerts.uncovered_spend', slug: 'uncovered-spend-email-alert'},
    {key: 'flex_indicator', labelKey: 'user_email_alerts.flex_indicator_critical', slug: 'flex-indicator-email-alert'},
    {key: 'goal', labelKey: 'user_email_alerts.goal_critical', slug: 'goal-email-alert'},
    {key: 'economics', labelKey: 'alerts.economics.settings.toggle', slug: 'economics-email-alert'},
];

const BY_KEY = Object.fromEntries(RECIPIENT_ALERTS.map((alert) => [alert.key, alert]));

// La Economía es un aviso semanal del cliente, no de una línea: no va en la configuración de la línea.
const ECONOMICS_KEY = 'economics';
const CLIENT_ONLY_KEYS = [ECONOMICS_KEY];
export const LINE_ALERT_KEYS = RECIPIENT_ALERTS.map((alert) => alert.key)
    .filter((key) => !CLIENT_ONLY_KEYS.includes(key));

const ODS_KEY_BY_LEVEL = {
    TYPE_ODS_VS_TODAY: 'ods_vs_today',
    TYPE_ODS_VS_YESTERDAY: 'ods_vs_yesterday',
    TYPE_ODS_VS_LAST_7_DAYS: 'ods_vs_last_7_days',
};
const ODS_UNSPLIT_KEY = 'ods_critic';

// Los `indicator` de la Bitácora que no se llaman como su aviso. Los registros viejos de ROAS/ACOS y las
// recuperaciones son del mismo aviso que su alerta.
const KEY_BY_INDICATOR = {
    RoasVsLastPeriod: 'roas_vs_last_period',
    CriticOnROASVsLastPeriod: 'roas_vs_last_period',
    AcosVsLastPeriod: 'acos_vs_last_period',
    CriticOnACOSVsLastPeriod: 'acos_vs_last_period',
    url_recovered: 'url_down',
    impressions_recovered: 'no_impressions',
    // El mail del presupuesto agotado sale por el interruptor de «hoy» (`notify_for_critic_ods_budget_exhausted`).
    ods_critic_budget_exhausted: 'ods_vs_today',
};

function has(alerts, key) {
    return Boolean(alerts) && Object.prototype.hasOwnProperty.call(alerts, key) && Array.isArray(alerts[key]);
}

/**
 * La clave de «quién recibe» de una alerta vigente (un ítem de `active-alerts`), o `null` si ese aviso no
 * tiene interruptor por persona (una regla propia, una cuenta desconectada) o el backend no la manda.
 */
export function recipientsKeyOf(item, alerts) {
    const indicator = item && item.indicator;
    if (!indicator || !alerts) return null;
    const level = item.detail && item.detail.level;
    const candidates = [];
    if (indicator === ODS_UNSPLIT_KEY && ODS_KEY_BY_LEVEL[level]) candidates.push(ODS_KEY_BY_LEVEL[level]);
    if (KEY_BY_INDICATOR[indicator]) candidates.push(KEY_BY_INDICATOR[indicator]);
    candidates.push(indicator);
    if (indicator === ODS_UNSPLIT_KEY || indicator === 'ods_critic_budget_exhausted') candidates.push(ODS_UNSPLIT_KEY);
    return candidates.find((key) => has(alerts, key)) || null;
}

const EVERY_KEY = Object.fromEntries([...RECIPIENT_ALERTS.map((alert) => alert.key), ODS_UNSPLIT_KEY]
    .map((key) => [key, []]));

/**
 * ¿Esta alerta puede tener «quién recibe»? Se contesta ANTES de pedirlo: una regla propia elige sus
 * destinatarios en la regla, y desplegarla sólo para no mostrar nada es un clic perdido.
 */
export function canHaveRecipients(item) {
    return recipientsKeyOf(item, EVERY_KEY) !== null;
}

/** El texto con que se nombra un aviso (`null` si no tiene: la clave sin partir de ODS). */
export function recipientLabelKey(key) {
    return BY_KEY[key] ? BY_KEY[key].labelKey : null;
}

/** El endpoint de su interruptor (`null` = no se puede prender desde acá). */
export function recipientToggleSlug(key) {
    return BY_KEY[key] ? BY_KEY[key].slug : null;
}

/**
 * Las personas del equipo que tienen el aviso prendido, en el orden del equipo. Quien está sólo por la
 * Economía no cuenta para los avisos de las líneas: no le llegan.
 */
export function recipientsFor(payload, key) {
    if (!payload || !has(payload.alerts, key)) return [];
    const ids = new Set(payload.alerts[key].map(String));
    return payload.team.filter((member) => member && ids.has(String(member.id))
        && (key === ECONOMICS_KEY || member.line_alerts !== false));
}

/**
 * El estado de quien mira para ese aviso: `null` si prenderlo en «Mis alertas» no le haría llegar ESTE
 * mail, así que no se le ofrece; si no, `{receives}`. No le llega si no está en el equipo del cliente, ni
 * —en los avisos de las líneas— si está en `team` sólo por la Economía (`line_alerts: false`: el Admin no
 * asignado, contrato §4.3).
 */
export function myRecipientState(payload, key, myUserId) {
    if (!payload || myUserId == null || !has(payload.alerts, key)) return null;
    const me = payload.team.find((member) => member && String(member.id) === String(myUserId));
    if (!me) return null;
    if (key !== ECONOMICS_KEY && me.line_alerts === false) return null;
    return {receives: payload.alerts[key].some((id) => String(id) === String(myUserId))};
}

/** El payload con quien mira agregado o quitado de un aviso (la actualización optimista). */
export function withMyRecipient(payload, key, myUserId, receives) {
    if (!payload || !has(payload.alerts, key)) return payload;
    const others = payload.alerts[key].filter((id) => String(id) !== String(myUserId));
    return {...payload, alerts: {...payload.alerts, [key]: receives ? [...others, myUserId] : others}};
}

/** Las claves de la configuración de la línea que el backend manda (un BE viejo no manda las nuevas). */
export function lineRecipientKeys(payload) {
    if (!payload) return [];
    return LINE_ALERT_KEYS.filter((key) => has(payload.alerts, key));
}

/** El nombre de una persona: el del equipo o, si viene vacío, su mail. */
export function memberName(member) {
    if (!member) return '';
    return (member.name && String(member.name).trim()) || member.email || '';
}

/**
 * Qué dice el renglón de una alerta de «Ahora» sobre su mail, a partir del `detail` de la Bitácora
 * (contrato §4.2). `recipient_count` es la verdad de «le llegó a alguien»; `emailed` sólo dice que el envío
 * salió. Devuelve `{kind: 'sent', count}`, `{kind: 'nobody'}` (se intentó y nadie del equipo lo tiene
 * prendido) o `null` cuando no hay nada honesto que decir: una alerta anterior al deploy (sin la clave), o un
 * mail que el gate frenó a propósito (`email_gate`: ya se había avisado) o que no salió.
 *
 * «A nadie» lo dice `no_recipients: true` (contrato §4.2), que escriben todos los emisores. Sin esa clave se
 * deduce de `emailed: true` con 0 destinatarios, que sólo vale donde `emailed` tiene la semántica del gate
 * (ritmo, excedido, ODS, flex, objetivos…): en impresiones, URL y reglas `emailed` dice otra cosa, y ahí,
 * sin la clave, no se afirma nada.
 */
export function mailSummaryOf(detail) {
    if (!detail || typeof detail.recipient_count !== 'number') return null;
    if (detail.recipient_count > 0) return {kind: 'sent', count: detail.recipient_count};
    if (detail.no_recipients === true) return {kind: 'nobody'};
    if (detail.emailed === true && !detail.email_gate) return {kind: 'nobody'};
    return null;
}
